import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ApiClient, Arbeitsstationen as Bestand,
                Arbeitsstationsausstattung } from '../api';
import { inDieAblage } from '../kopieren';
import { vorWieLange } from './Zustandsseite';
import { IconPlus, IconSchluessel, IconHerunterladen } from '../Icons';
import {
  ConfirmDialog, ContentSection, DataTable, EmptyState, ErrorState, Feld,
  FormSection, StatusBadge, type Spalte, type Zustand,
} from '../muster';

type Arbeitsplatz = Bestand['arbeitsplaetze'][number];

/**
 * Die Hervorhebung des Schlüsselkastens.
 *
 * ZUORDNUNG (19.09.2026): Das ist KEIN Zustand. Der Kasten meldet keinen
 * Fehler und keinen Konflikt - er hebt einen Satz hervor, den man lesen muss,
 * bevor man das Fenster schliesst. Also der Akzent und keine Zustandsfarbe.
 *
 * BEFUND, nicht mitrepariert: Die Farbe wird derzeit gar nicht dargestellt.
 * `.hinweis` hat ohne Zusatzklasse keinen Rand (`styles.css`), und
 * `borderColor` allein zeichnet ohne `border-style` nichts. Der Kasten ist
 * seit jeher randlos; wer das ändern will, gibt ihm eine der vorhandenen
 * Klassen (`hinweis warnung`) - das ist eine Gestaltungsentscheidung und
 * gehoert nicht in einen Markenwechsel.
 */
const ROT = 'var(--akzent-farbe)';

/**
 * Die Zeichenarbeitsplätze.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM DAS NEBEN DIE MASCHINEN GEHÖRT
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Ein Arbeitsplatz ist dasselbe wie eine Verarbeitungsmaschine, nur mit einem
 * Menschen davor: ein Rechner, der sich mit einem Schlüssel meldet, eine
 * Fassung fährt und aktualisiert werden will.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DIE EINZIGE FRAGE, DIE DIESE SEITE BEANTWORTEN MUSS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * „Fährt jeder die aktuelle Fassung?" Alles andere ist Beiwerk. Deshalb steht
 * die Fassung in der zweiten Spalte und nicht am Ende, und deshalb wird sie
 * rot, wenn sie abweicht.
 */
export function Arbeitsstationen({ api }: { api: ApiClient }) {
  const [bestand, setBestand] = useState<Bestand | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [anlegen, setAnlegen] = useState(false);

  // Nach dem Anlegen oder Erneuern: Der Schlüssel steht genau hier und
  // nirgends sonst. Verschwindet er, ist er weg — gespeichert wird nur sein
  // Hashwert.
  const [ausstattung, setAusstattung] =
    useState<Arbeitsstationsausstattung | null>(null);

  /** Offene Nachfrage: Entfernen und Schlüsselwechsel sind nicht umkehrbar. */
  const [frage, setFrage] =
    useState<{ station: Arbeitsplatz; art: 'loeschen' | 'schluessel' } | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  const laden = useCallback(() => {
    setFehler(null);
    return api.arbeitsstationen().then(setBestand).catch(setFehler);
  }, [api]);

  useEffect(() => { laden(); }, [laden]);

  const schalten = useCallback(async (
    id: string, was: 'sperren' | 'freigeben' | 'loeschen',
  ) => {
    try {
      await api.arbeitsstationSchalten(id, was);
      await laden();
    } catch (e) {
      setFehler(e);
    }
  }, [api, laden]);

  const spalten: Spalte<Arbeitsplatz>[] = useMemo(() => [
    {
      schluessel: 'name', titel: 'Rechner', immerSichtbar: true,
      wert: (a) => a.name,
      inhalt: (a) => (
        <>
          <strong>{a.name}</strong>
          {a.bemerkung && (
            <div className="schwach" style={{ fontSize: 11 }}>{a.bemerkung}</div>
          )}
        </>
      ),
    },

    /* Die Fassung in der zweiten Spalte: Sie ist die Frage, wegen der die
       Seite gebaut wird. Sie war bis U6a nur rot eingefärbt — jetzt trägt
       sie Zeichen und Text (Abschnitt 10). */
    {
      schluessel: 'fassung', titel: 'Addin-Fassung', breite: '190px',
      wert: (a) => a.addinFassung ?? '',
      inhalt: (a) => {
        if (!a.addinFassung) {
          return <em className="schwach">noch nie gemeldet</em>;
        }
        return a.istAktuell
          ? <span>{a.addinFassung}</span>
          : (
            <StatusBadge
              zustand="warnung" klein
              text={`${a.addinFassung} — veraltet`}
              titel="Nicht die freigegebene Fassung"
            />
          );
      },
    },
    {
      schluessel: 'benutzer', titel: 'Benutzer',
      wert: (a) => a.benutzer ?? '',
      inhalt: (a) => <span className="schwach">{a.benutzer ?? '—'}</span>,
    },
    {
      schluessel: 'revit', titel: 'Revit', breite: '90px',
      wert: (a) => a.revitJahr ?? '',
      inhalt: (a) => <span className="schwach">{a.revitJahr ?? '—'}</span>,
    },
    {
      schluessel: 'meldung', titel: 'zuletzt gemeldet', breite: '150px',
      wert: (a) => a.letzteMeldung ?? '',
      inhalt: (a) => (
        <span className="schwach" title={a.letzteMeldung ?? undefined}>
          {a.letzteMeldung ? vorWieLange(a.letzteMeldung) : '—'}
        </span>
      ),
    },
    {
      schluessel: 'zustand', titel: 'Zustand', art: 'status', breite: '120px',
      wert: (a) => stationszustand(a).text,
      inhalt: (a) => {
        const z = stationszustand(a);
        return <StatusBadge zustand={z.zustand} text={z.text} klein />;
      },
    },
    {
      schluessel: 'aktionen', titel: '', breite: '230px', sortierbar: false,
      ausrichtung: 'rechts',
      inhalt: (a) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          <button
            className="m-knopf"
            title="Neuen Zugangsschlüssel erzeugen"
            onClick={(e) => { e.stopPropagation(); setFrage({ station: a, art: 'schluessel' }); }}
          >
            Schlüssel
          </button>{' '}
          {a.gesperrt ? (
            <button className="m-knopf"
                    onClick={(e) => { e.stopPropagation(); schalten(a.id, 'freigeben'); }}>
              freigeben
            </button>
          ) : (
            <button className="m-knopf" title="Keine Aktualisierung mehr ausliefern"
                    onClick={(e) => { e.stopPropagation(); schalten(a.id, 'sperren'); }}>
              sperren
            </button>
          )}{' '}
          <button
            className="m-knopf"
            onClick={(e) => { e.stopPropagation(); setFrage({ station: a, art: 'loeschen' }); }}
          >
            entfernen
          </button>
        </span>
      ),
    },
  ], [schalten]);

  return (
    <>
      {ausstattung && (
        <Ausstattungsanzeige
          api={api}
          daten={ausstattung}
          schliessen={() => { setAusstattung(null); laden(); }}
        />
      )}

      {anlegen && (
        <Anlegen
          api={api}
          schliessen={() => setAnlegen(false)}
          fertig={(a) => { setAnlegen(false); setAusstattung(a); }}
        />
      )}

      <ContentSection
        titel="Arbeitsstationen"
        beschreibung={'Rechner mit dem TuV-Addin. Beim Anlegen entsteht ein '
          + 'Zugangsschlüssel — er wird genau einmal angezeigt, zusammen mit der '
          + 'Anleitung, die der Mitarbeiter braucht. Installiert wird per '
          + 'Doppelklick; Administratorrechte sind dafür nicht nötig. Danach holen '
          + 'sich die Rechner neue Fassungen selbst.'}
        meta={bestand?.aktuelleFassung
          ? <>Freigegeben: <strong>{bestand.aktuelleFassung}</strong></>
          : undefined}
        aktionen={(
          <button className="m-knopf m-knopf-haupt" onClick={() => setAnlegen(true)}>
            <IconPlus size={16} />Arbeitsstation anlegen
          </button>
        )}
        nackt
      >
        {(bestand?.hinweise ?? []).map((h, i) => (
          <div key={i} className="hinweis">{h}</div>
        ))}

        <DataTable
          ausfuhrDateiname="Arbeitsstationen"
          spalten={spalten}
          zeilen={bestand?.arbeitsplaetze ?? []}
          schluessel={(a) => a.id}
          laedt={bestand === null && fehler === null}
          fehler={bestand === null ? fehler : undefined}
          beiErneutVersuchen={laden}
          ausfuhr
          leer={(
            <EmptyState
              icon={<IconSchluessel />}
              titel="Noch keine Arbeitsstation angelegt."
              erklaerung={'„Anlegen" erzeugt den Zugangsschlüssel und die Anleitung '
                + 'für den Mitarbeiter.'}
              aktion={(
                <button className="m-knopf m-knopf-haupt" onClick={() => setAnlegen(true)}>
                  <IconPlus size={16} />Arbeitsstation anlegen
                </button>
              )}
            />
          )}
        />

        {fehler !== null && bestand !== null && (
          <ErrorState klein meldung="Die letzte Aktion ist fehlgeschlagen."
                      technisch={fehler} />
        )}
      </ContentSection>

      <ConfirmDialog
        offen={frage?.art === 'loeschen'}
        art="loeschen"
        titel={`${frage?.station.name ?? ''} entfernen?`}
        text={'Sie verschwindet aus der Liste; ihr Verlauf bleibt erhalten.'}
        laeuft={laeuft}
        beiBestaetigen={async () => {
          const s = frage?.station;
          setFrage(null);
          if (!s) return;
          setLaeuft(true);
          await schalten(s.id, 'loeschen');
          setLaeuft(false);
        }}
        beiAbbrechen={() => setFrage(null)}
      />

      <ConfirmDialog
        offen={frage?.art === 'schluessel'}
        art="warnung"
        titel={`Für ${frage?.station.name ?? ''} einen neuen Schlüssel erzeugen?`}
        text={'Der bisherige gilt dann nicht mehr — die Arbeitsstation bekommt '
          + 'keine Aktualisierung, bis der neue eingetragen ist.'}
        bestaetigenText="Neuen Schlüssel erzeugen"
        laeuft={laeuft}
        beiBestaetigen={async () => {
          const s = frage?.station;
          setFrage(null);
          if (!s) return;
          setLaeuft(true);
          try {
            setAusstattung(await api.arbeitsstationSchluessel(s.id));
          } catch (e) {
            setFehler(e);
          } finally {
            setLaeuft(false);
          }
        }}
        beiAbbrechen={() => setFrage(null)}
      />
    </>
  );
}

/** „still" ist kein Fehler: Der Rechner ist aus, nicht kaputt (Abschnitt 18). */
function stationszustand(a: Arbeitsplatz): { text: string; zustand: Zustand } {
  if (a.gesperrt) return { text: 'gesperrt', zustand: 'fehler' };
  if (a.erreichbar) return { text: 'aktiv', zustand: 'gut' };
  return { text: 'still', zustand: 'neutral' };
}

// ═══════════════════════════════════════════════════════════════════════════
// DER SCHLÜSSEL — UND DIE ANLEITUNG FÜR DEN MITARBEITER
// ═══════════════════════════════════════════════════════════════════════════
//
// Diese Karte erscheint genau EINMAL je Arbeitsstation. Danach lässt sich der
// Schlüssel nicht mehr nachschlagen — auch von einem Verwalter nicht, denn in
// der Datenbank steht nur sein Hashwert.
//
// Sie muss deshalb alles enthalten, was WEITERGEGEBEN wird. Nicht nur den
// Schlüssel: Wer ihn allein bekommt, weiß nicht, wohin damit. Die Anleitung
// steht als fertiger Text daneben und lässt sich mit einem Klick in die
// Ablage nehmen — zum Einfügen in eine Mail oder eine Teams-Nachricht.
//
// Die frühere Fassung zeigte hier stattdessen den Inhalt einer
// einstellungen.json und den Pfad, an den sie von Hand zu legen war. Das
// entfällt: Den Schlüssel fragt das Einrichtungsprogramm selbst ab.

function Anleitungstext(name: string, schluessel: string, jahr: string) {
  return [
    `Einrichtung des TuV Revit Add-ins auf ${name}`,
    '',
    'Was Sie brauchen: die ZIP-Datei aus der Plattform und den Schlüssel',
    'unten. Administratorrechte brauchen Sie NICHT.',
    '',
    '1. ZIP-Datei auspacken — irgendwohin, z. B. auf den Desktop.',
    '   Der Ort spielt keine Rolle.',
    '',
    '2. TuvAddinAktualisierer.exe doppelklicken.',
    '',
    '3. Diesen Schlüssel in das Feld einfügen:',
    '',
    `      ${schluessel}`,
    '',
    '4. Auf „Installieren …" klicken und warten. Es dauert bis zu zwei',
    '   Minuten — das Add-in wird dabei heruntergeladen.',
    '',
    '5. Revit beenden und neu starten. Add-ins werden nur beim Start',
    '   geladen; ohne den Neustart fehlt die Schaltfläche, auch wenn',
    '   alles richtig installiert ist.',
    '',
    'Danach finden Sie im Menüband den Reiter „TuV Export".',
    '',
    'Um Updates müssen Sie sich nicht kümmern — das Programm holt sie ab',
    'jetzt von selbst. Läuft Revit gerade, fragt es vorher.',
    '',
    `Revit-Jahrgang dieser Arbeitsstation: ${jahr}`,
    '',
    'Den Schlüssel bitte nicht weitergeben: Er gilt für genau diesen',
    'Rechner. Geht er verloren, kann die BIM-Betreuung einen neuen',
    'erzeugen — der bisherige gilt dann nicht mehr.',
  ].join('\n');
}

function Ausstattungsanzeige(
  { api, daten, schliessen }:
  {
    api: ApiClient;
    daten: Arbeitsstationsausstattung;
    schliessen: () => void;
  },
) {
  // Ob schon kopiert wurde. Der Knopf bleibt danach benutzbar: Ein Knopf, der
  // sich nach dem ersten Klick sperrt, verliert den Schlüssel endgültig, wenn
  // das Einfügen misslingt — und das kommt vor. Die Anzeige merkt sich den
  // Klick nur, um zu zeigen, dass er angekommen ist.
  const [kopiert, setKopiert] = useState<'' | 'schluessel' | 'anleitung'>('');

  const jahr = daten.revitJahr ?? '2025';
  const anleitung = Anleitungstext(daten.name, daten.schluessel, jahr);

  const kopieren = async (text: string, was: 'schluessel' | 'anleitung') => {
    const geklappt = await inDieAblage(text);

    if (geklappt) {
      setKopiert(was);
      window.setTimeout(() => setKopiert(''), 2500);
      return;
    }

    // Rückfallweg: Manche Browser erlauben das Kopieren nur nach einer
    // direkten Geste oder gar nicht. Ohne diesen Zweig wäre der Schlüssel
    // dann verloren, ohne dass es jemand merkt.
    alert('Der Browser erlaubt das Kopieren nicht. Bitte von Hand '
        + 'markieren:\n\n' + text);
  };

  return (
    <div className="ueberlagerung">
      <div className="karte" style={{ width: 720, margin: 0,
                                      textAlign: 'left' }}>
        <h2 className="karte-titel">
          <IconSchluessel />{daten.name} — Zugangsschlüssel
        </h2>

        <div className="hinweis" style={{ borderColor: ROT }}>
          Dieser Schlüssel wird <strong>nicht noch einmal angezeigt</strong>.
          Kopieren Sie ihn jetzt und geben Sie ihn dem Mitarbeiter. Geht er
          verloren, erzeugen Sie hier einen neuen — der bisherige gilt dann
          nicht mehr.
        </div>

        {/* ── Der Schlüssel, gross und in fester Zeichenbreite ───────────
            Feste Zeichenbreite, weil der Schlüssel Zeichen enthält, die in
            einer Proportionalschrift kaum zu unterscheiden sind: l und 1,
            O und 0. Wer ihn vorliest oder abtippt, verliert sich sonst. */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'stretch',
                      margin: '12px 0' }}>
          <code style={{ flex: 1, background: '#f4f4f4', padding: '12px 14px',
                         fontSize: 15, letterSpacing: 0.5, borderRadius: 4,
                         wordBreak: 'break-all' }}>
            {daten.schluessel}
          </code>

          <button className="m-knopf m-knopf-haupt" style={{ whiteSpace: 'nowrap' }}
                  onClick={() => void kopieren(daten.schluessel, 'schluessel')}>
            {kopiert === 'schluessel' ? '✓ kopiert' : 'Schlüssel kopieren'}
          </button>
        </div>

        {/* ── Was der Mitarbeiter tun muss ───────────────────────────────
            Der Auftrag dazu war ausdrücklich: beim Anlegen „eine
            beschreibung über die schritte wegen schlüssel usw. für
            mitarbeiter ausgeben". Sie steht hier lesbar UND daneben als
            Text zum Weitergeben — wer sie nur liest, tippt sie sonst ab. */}
        <h3 style={{ fontSize: 13, margin: '18px 0 6px' }}>
          Was der Mitarbeiter tun muss
        </h3>

        <ol style={{ fontSize: 12.5, lineHeight: 1.7, paddingLeft: 20,
                     margin: 0 }}>
          <li>
            Die ZIP-Datei auspacken — irgendwohin, der Ort spielt keine
            Rolle.
          </li>
          <li>
            <code>TuvAddinAktualisierer.exe</code> doppelklicken. Es öffnet
            sich ein Einrichtungsfenster.
          </li>
          <li>
            Den Schlüssel oben in das Feld einfügen und auf
            <strong> Installieren …</strong> klicken.
          </li>
          <li>
            Warten — bis zu zwei Minuten. Das Add-in wird dabei
            heruntergeladen, in den Revit-Ordner eingebaut, bei Revit
            angemeldet und der Autostart eingerichtet.
          </li>
          <li>
            <strong>Revit beenden und neu starten.</strong> Add-ins werden
            nur beim Start geladen — ohne den Neustart fehlt die
            Schaltfläche, auch wenn alles richtig installiert ist.
          </li>
        </ol>

        <p className="sehr-schwach" style={{ fontSize: 12, marginTop: 10 }}>
          <strong>Administratorrechte sind nicht nötig.</strong> Es wird nur
          in das Benutzerprofil geschrieben: das Programm nach
          <code> %LocalAppData%\TuV\TuvAktualisierer</code>, das Add-in nach
          <code> %AppData%\Autodesk\Revit\Addins\{jahr}</code>, der Autostart
          unter <code>HKEY_CURRENT_USER</code>. Kommt trotzdem eine
          Rechteabfrage, stimmt etwas nicht.
        </p>

        <p className="sehr-schwach" style={{ fontSize: 12 }}>
          Um Updates muss sich niemand kümmern. Dieselbe exe erkennt bei
          einem späteren Start, dass der Rechner schon eingerichtet ist,
          fragt <em>nicht</em> wieder nach dem Schlüssel und wendet nur das
          Update an. Der Schlüssel steckt <strong>nicht</strong> im
          Download — die ZIP-Datei darf deshalb weitergegeben und aufgehoben
          werden.
        </p>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end',
                      marginTop: 16, flexWrap: 'wrap' }}>
          <button className="m-knopf" onClick={() => void kopieren(anleitung, 'anleitung')}>
            {kopiert === 'anleitung'
              ? '✓ kopiert'
              : 'Anleitung mit Schlüssel kopieren'}
          </button>

          <button className="m-knopf" onClick={() => void api.arbeitsstationPaket(
                    daten.id, daten.name, daten.schluessel)}>
            <IconHerunterladen size={15} />
            Download: Programm und Anleitung
          </button>

          <button className="m-knopf m-knopf-haupt" onClick={schliessen}>
            Ich habe den Schlüssel gesichert
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ANLEGEN
// ═══════════════════════════════════════════════════════════════════════════

function Anlegen(
  { api, schliessen, fertig }:
  {
    api: ApiClient;
    schliessen: () => void;
    fertig: (a: Arbeitsstationsausstattung) => void;
  },
) {
  const [name, setName] = useState('');
  const [benutzer, setBenutzer] = useState('');
  const [revitJahr, setRevitJahr] = useState('2025');
  const [bemerkung, setBemerkung] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<unknown>(null);
  const [namensfehler, setNamensfehler] = useState<string | null>(null);

  const absenden = async () => {
    if (!name.trim()) { setNamensfehler('Der Rechnername fehlt.'); return; }

    setLaeuft(true); setFehler(null); setNamensfehler(null);

    try {
      fertig(await api.arbeitsstationAnlegen({
        name: name.trim(),
        benutzer: benutzer.trim() || undefined,
        revitJahr: revitJahr.trim() || undefined,
        bemerkung: bemerkung.trim() || undefined,
      }));
    } catch (e) {
      setFehler(e);
      setLaeuft(false);
    }
  };

  return (
    <FormSection
      titel="Neue Arbeitsstation"
      beschreibung={'Der Rechnername muss mit dem tatsächlichen übereinstimmen — der '
        + 'Aktualisierer meldet ihn, und daran wird die Station wiedererkannt. Unter '
        + 'Windows steht er unter „Systeminfo" oder in der Eingabeaufforderung als '
        + 'hostname.'}
      spalten={2}
      fuss={(
        <>
          <button className="m-knopf" onClick={schliessen} disabled={laeuft}>
            Abbrechen
          </button>
          <button className="m-knopf m-knopf-haupt" onClick={absenden} disabled={laeuft}>
            {laeuft ? 'legt an …' : 'Anlegen und Schlüssel erzeugen'}
          </button>
        </>
      )}
    >
      {fehler !== null && (
        <ErrorState klein meldung="Die Arbeitsstation konnte nicht angelegt werden."
                    technisch={fehler} />
      )}

      <Feld
        beschriftung="Rechnername"
        wert={name}
        beiAenderung={(w) => setName(w as string)}
        platzhalter="z. B. TV-CAD-07"
        pflicht
        fehler={namensfehler ?? undefined}
      />
      <Feld
        beschriftung="Benutzer"
        wert={benutzer}
        beiAenderung={(w) => setBenutzer(w as string)}
        platzhalter="optional"
      />
      <Feld
        beschriftung="Revit-Jahr"
        wert={revitJahr}
        beiAenderung={(w) => setRevitJahr(w as string)}
        hinweis={'Bestimmt den Addin-Ordner. Ein Addin im falschen Jahresordner wird '
          + 'nie geladen, ohne jede Fehlermeldung.'}
      />
      <Feld
        beschriftung="Bemerkung"
        wert={bemerkung}
        beiAenderung={(w) => setBemerkung(w as string)}
        platzhalter="optional"
        breite="voll"
      />
    </FormSection>
  );
}
